/**
 * IELTS V2 — Results Centre
 * Route: /resources/ielts/results/:attempt_id
 *
 * Post-submit review of a Listening Simulation attempt. Band conversion,
 * per-part & per-question-type analytics, review answers with transcript
 * evidence and full collapsible transcripts, retry (creates NEW attempt).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, XCircle, Award, TrendingUp, TrendingDown,
  ArrowLeft, RotateCcw, ChevronDown, ChevronUp, Sparkles, Compass,
  Volume2, ListChecks,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const TYPE_LABEL = {
  multiple_choice: 'Multiple Choice',
  short_answer: 'Sentence / Note Completion',
  true_false_not_given: 'True / False / Not Given',
  matching: 'Matching',
  yes_no_not_given: 'Yes / No / Not Given',
  mcsa: 'Multiple Choice (single answer)',
};

function labelType(t) { return TYPE_LABEL[t] || (t || '').replace(/_/g, ' '); }
function pct(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return `${Math.round(v)}%`;
}

export default function IeltsResultsCentrePage() {
  const { attempt_id } = useParams();
  const nav = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTranscripts, setShowTranscripts] = useState({});
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/ielts/v2/listening/simulation/results/${attempt_id}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to load results');
      setData(null);
    } finally { setLoading(false); }
  }, [attempt_id]);
  useEffect(() => { load(); }, [load]);

  const startRetry = async () => {
    setStarting(true);
    try {
      // If an in_progress exists, resume it; else start creates a fresh attempt
      await ax.post('/api/ielts/v2/listening/simulation/start', {
        variant: data?.attempt?.variant || 'academic',
      });
      nav('/resources/ielts/listening/simulation');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start a new attempt');
    } finally { setStarting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        <div className="pt-32 pb-16 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading your results…
        </div>
        <Footer />
      </div>
    );
  }
  if (!data?.attempt) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        <div className="pt-32 pb-16 text-center">
          <div className="text-slate-500">Attempt not found.</div>
          <button onClick={() => nav('/resources/ielts')}
            className="mt-4 text-indigo-600 font-bold text-sm">← Back to IELTS Hub</button>
        </div>
        <Footer />
      </div>
    );
  }
  const a = data.attempt;
  const r = a.results || {};
  const parts = data.parts || [];
  const dResults = a.detailed_results || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        {/* Breadcrumb */}
        <button onClick={() => nav('/resources/ielts')}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="ielts-results-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to IELTS Hub
        </button>

        {/* Headline card */}
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-500 p-6 md:p-8 text-white shadow-xl"
          data-testid="ielts-results-headline">
          <div className="text-[11px] uppercase tracking-widest text-indigo-100 font-bold">
            IELTS Listening · Simulation Result
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-sm text-indigo-100">Raw Score</div>
              <div className="text-6xl md:text-7xl font-black tabular-nums" data-testid="ielts-results-raw">
                {r.raw_score ?? 0}<span className="text-indigo-200 text-3xl font-black"> / {r.total_questions ?? 40}</span>
              </div>
              <div className="text-sm text-indigo-100 mt-1">Accuracy: <b>{pct(r.accuracy)}</b></div>
            </div>
            <div className="md:text-right">
              <div className="text-sm text-indigo-100">{r.estimated_band_label || 'Winhouse Estimated IELTS Practice Band'}</div>
              <div className="text-6xl md:text-8xl font-black tabular-nums" data-testid="ielts-results-band">
                {(r.estimated_band ?? 0).toFixed(1)}
              </div>
              <div className="text-[11px] text-indigo-100 max-w-xs md:ml-auto mt-1 leading-relaxed">
                {r.estimated_band_disclaimer}
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={startRetry} disabled={starting}
              data-testid="ielts-results-retry"
              className="px-5 py-2 rounded-full bg-white text-indigo-700 hover:bg-indigo-50 font-black text-sm inline-flex items-center gap-2 disabled:opacity-50 transition">
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Retry Simulation (new attempt)
            </button>
            <button onClick={() => nav('/resources/ielts')}
              className="px-4 py-2 rounded-full bg-indigo-800/40 hover:bg-indigo-800/60 text-white text-sm font-bold">
              Return to Hub
            </button>
          </div>
        </div>

        {/* Per-part performance */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="ielts-results-per-part">
          {(r.per_part || []).map((p) => (
            <div key={p.part_number} className="rounded-xl bg-white border border-slate-200 p-4"
              data-testid={`ielts-results-part-${p.part_number}`}>
              <div className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold">Part {p.part_number}</div>
              <div className="text-sm font-bold text-[#172033] mt-0.5 leading-tight">{p.title}</div>
              <div className="mt-2 text-3xl font-black tabular-nums text-[#172033]">
                {p.correct}<span className="text-slate-400 text-xl">/{p.total}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-indigo-500" style={{ width: `${p.percentage || 0}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">{pct(p.percentage)} accuracy</div>
            </div>
          ))}
        </div>

        {/* Analytics grid */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <StrengthCard title="Strongest Skill" icon={<TrendingUp className="h-4 w-4 text-emerald-600" />}
            value={r.strongest_skill} accent="text-emerald-600" testid="ielts-results-strongest" />
          <StrengthCard title="Focus Area" icon={<TrendingDown className="h-4 w-4 text-rose-600" />}
            value={r.weakest_skill} accent="text-rose-600" testid="ielts-results-focus"
            sub={r.weakest_skill_accuracy != null ? `${pct(r.weakest_skill_accuracy)} accuracy` : null} />
          <RecommendCard rec={r.recommendation} onGoHub={() => nav('/resources/ielts')} />
        </div>

        {/* Per question-type breakdown */}
        <div className="mt-6 rounded-xl bg-white border border-slate-200 p-4 md:p-6" data-testid="ielts-results-by-type">
          <div className="flex items-center gap-2 text-sm font-bold text-[#172033]">
            <ListChecks className="h-4 w-4 text-indigo-500" /> Performance by question type
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(r.per_type || {}).map(([t, v]) => {
              const acc = v.total ? Math.round(100 * v.correct / v.total) : 0;
              return (
                <div key={t} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-xs font-bold text-[#172033]">{labelType(t)}</div>
                  <div className="mt-1 text-2xl font-black tabular-nums text-[#172033]">
                    {v.correct}<span className="text-slate-400 text-lg">/{v.total}</span>
                    <span className="ml-2 text-sm text-slate-500 font-bold">{acc}%</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${acc >= 70 ? 'bg-emerald-500' : acc >= 40 ? 'bg-amber-500' : 'bg-rose-500'}`}
                       style={{ width: `${acc}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Review Answers */}
        <ReviewAnswers dResults={dResults} parts={parts}
          showTranscripts={showTranscripts} setShowTranscripts={setShowTranscripts} />

        <div className="mt-6 text-[11px] text-slate-500 italic text-center max-w-2xl mx-auto">
          {r.estimated_band_disclaimer}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function StrengthCard({ title, icon, value, accent, sub, testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
        {icon}{title}
      </div>
      <div className={`mt-2 text-lg font-black ${accent}`}>{value || '—'}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RecommendCard({ rec, onGoHub }) {
  if (!rec) return null;
  return (
    <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4" data-testid="ielts-results-recommendation">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-700">
        <Compass className="h-4 w-4" /> Recommended next practice
      </div>
      <div className="mt-2 text-sm font-bold text-[#172033]">{rec.label || 'Keep practising'}</div>
      <div className="mt-1 text-xs text-slate-600">{rec.reason}</div>
      <button onClick={onGoHub}
        data-testid="ielts-results-recommendation-cta"
        className="mt-3 text-xs font-bold text-indigo-700 hover:text-indigo-900">
        Open practice hub →
      </button>
    </div>
  );
}

function ReviewAnswers({ dResults, parts, showTranscripts, setShowTranscripts }) {
  const byPart = useMemo(() => {
    const g = {};
    (dResults || []).forEach((r) => {
      const p = r.part_number || 0;
      (g[p] = g[p] || []).push(r);
    });
    return g;
  }, [dResults]);
  return (
    <div className="mt-6 rounded-xl bg-white border border-slate-200 p-4 md:p-6" data-testid="ielts-results-review">
      <div className="flex items-center gap-2 text-sm font-bold text-[#172033]">
        <Sparkles className="h-4 w-4 text-indigo-500" /> Review answers
      </div>
      <div className="mt-4 space-y-6">
        {parts.map((p) => {
          const results = byPart[p.part_number] || [];
          const correct = results.filter((r) => r.correct).length;
          const open = showTranscripts[p.part_number];
          return (
            <div key={p.part_number} className="border border-slate-200 rounded-lg">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold">Part {p.part_number}</div>
                  <div className="text-sm font-bold text-[#172033]">{p.title}</div>
                </div>
                <div className="text-xs text-slate-500 tabular-nums">
                  <b className="text-[#172033]">{correct}</b>/{results.length}
                </div>
              </div>
              <div className="p-3 space-y-2">
                {results.map((r) => (
                  <div key={r.question_id}
                    className={`rounded-md border p-3 ${r.correct ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                    data-testid={`ielts-result-${r.question_id}`}>
                    <div className="flex items-center gap-2 text-xs font-bold text-[#172033]">
                      {r.correct ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
                      Q{r.number} · {labelType(r.question_type)}
                    </div>
                    <div className="mt-1 text-sm"><b>Your answer:</b> {String(r.your_answer ?? '—')}</div>
                    {!r.correct && <div className="text-sm"><b>Correct:</b> {r.correct_answer}</div>}
                    {r.word_limit_note && <div className="text-xs text-amber-700 mt-1">{r.word_limit_note}</div>}
                    {r.explanation && <div className="text-xs text-slate-700 mt-1"><b>Why:</b> {r.explanation}</div>}
                    {r.transcript_evidence && (
                      <div className="text-xs text-slate-600 italic mt-1">Transcript: "{r.transcript_evidence}"</div>
                    )}
                  </div>
                ))}
                <button onClick={() => setShowTranscripts((m) => ({ ...m, [p.part_number]: !m[p.part_number] }))}
                  data-testid={`ielts-results-transcript-toggle-${p.part_number}`}
                  className="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
                  <Volume2 className="h-3.5 w-3.5" />
                  {open ? 'Hide' : 'Show'} full transcript
                  {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                {open && (
                  <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-3 whitespace-pre-wrap text-xs text-slate-700"
                    data-testid={`ielts-results-transcript-${p.part_number}`}>
                    {p.transcript || 'Transcript not available.'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
